import React, {
  useCallback,
  useMemo,
  useRef,
  useEffect,
  useState,
} from 'react';
import { createEditor, Editor, Element, Transforms } from 'slate';
import { withHistory } from 'slate-history';
import { Editable, ReactEditor, Slate, withReact } from 'slate-react';
import { RawDraftContentState } from 'draft-js';
import { AulaPlugin, composePlugins } from './plugins/plugins';
import { HoveringToolbar } from './components/HoveringToolbar/HoveringToolbar';
import { EditorWrapper, ControlsContainer } from './Editor.styled';
import { EditorState, isAulaEditorContent } from './Editor.types';
import {
  AulaEditorContextProvider,
  ContextState,
  useAulaEditorContext,
} from './EditorContext';
import { richtext } from './plugins/richtext/richtext';
import { headings } from './plugins/headings/headings';
import { blockQuote } from './plugins/blockQuote/blockQuote';
import { inlineLink } from './plugins/inlineLink/inlineLink';
import { blockLink } from './plugins/blockLink/blockLink';
import { handleReturn } from './plugins/handleReturn/handleReturn';
import { unsupported } from './plugins/unsupported/unsupported';
import { blockMenu } from './plugins/blockMenu/blockMenu';
import { latex } from './plugins/latex/latex';
import { convertLegacyContent } from './lib/convertLegacyContent';
import { file } from './plugins/file/file';
import { rootElement } from './plugins/rootElement/rootElement';
import { Spacer } from './components/Spacer/Spacer';
import { DropTarget } from './components/DropTarget/DropTarget';
import { handleDrop } from './plugins/handleDrop/handleDrop';
import { handleBlockMove } from './lib/handleBlockMove';
import { insertFilesFromEvent } from './lib/insertFilesFromEvent';
import { dragHandle } from './plugins/dragHandle/dragHandle';
import { InlineLink } from './components/InlineLink/InlineLink';
import { getCurrentFragment, hasSelection } from './lib/queries';
import { isInlineLink } from './plugins/inlineLink/inlineLinkType';
import { voidEditing } from './plugins/voidEditing/voidEditing';
import { codeBlock } from './plugins/code/code';
import { oneDrive } from './plugins/oneDrive/oneDrive';
import { oneDriveBusiness } from './plugins/oneDrive/oneDriveBusiness';
import { googleDrive } from './plugins/googleDrive/googleDrive';
import { AulaEditorInstance, EditorConfig, ContentTemplate } from './types';
import { startLocation } from './utils';
import {
  EditorActionButton,
  EditorActionButtonProps,
} from './components/EditorActionButton/EditorActionButton';
import { handleBackspace } from './plugins/handleBackspace/handleBackspace';
import { handleDownArrow } from './plugins/handleDownArrow/handleDownArrow';
import { plainTextMenuItem } from './plugins/plainTextMenuItem/plainTextMenuItem';
import { convertUnsupported } from './lib/convertUnsupported';
import { audio } from './plugins/audio/audio';
import { handleCopy } from './plugins/handleCopy/handleCopy';
import { handlePaste } from './plugins/handlePaste/handlePaste';
import { list } from './plugins/list/list';
import { AudioModal } from './components/AudioModal/AudioModal';
import { orderedListType, unorderedListType } from './plugins/list/listType';
import { handleShiftSelection } from './plugins/handleShiftSelection/handleShiftSelection';
import { useEffectIfWindowIsFocused } from './lib/useEffectIfWindowIsFocused';

type AulaEditableProps = {
  plugins?: AulaPlugin[];
  readOnly?: boolean | false;
  editor: AulaEditorInstance;
  placeholder?: string;
  onReturn?: (editorState: EditorState) => void;
  actionButton?: EditorActionButtonProps;
  cursorPosition?: 'start' | 'end';
  autoFocus?: boolean;
  templatesDrawer?: React.ElementType;
};

export type AulaEditorProps = {
  editorState: RawDraftContentState | EditorState;
  config?: EditorConfig;
  onChange?: (nextValue: EditorState) => void;
  editor?: AulaEditorInstance;
  cursorPosition?: 'start' | 'end';
  autoFocus?: boolean;
  templates?: ContentTemplate[];
  contentType?: string;
} & Omit<AulaEditableProps, 'editor'>;

const defaultTemplates: ContentTemplate[] = [];

// function to force Slate to re-render
const useForceUpdate = () => {
  const [, setState] = useState<number>(0);

  const forceUpdate = useCallback(() => {
    setState((n) => n + 1);
  }, []);

  return forceUpdate;
};

const AulaEditable = ({
  plugins,
  readOnly,
  editor,
  placeholder = 'Start typing',
  onReturn,
  actionButton,
  cursorPosition = 'end',
  autoFocus = true,
  templatesDrawer: TemplatesDrawer,
}: AulaEditableProps) => {
  const context = useAulaEditorContext();
  const {
    createEntity,
    updateEntity,
    deleteEntity,
    entities,
    config,
    draggingElement,
    setDraggingElement,
    setRecordingPath,
    recordingPath,
    setTemplatesPath,
    templatesPath,
    templates,
    contentType,
  } = context;

  const editorProps = useMemo(() => {
    const defaultPlugins = [
      richtext,
      headings,
      blockQuote,
      inlineLink,
      handleBackspace,
      handleDownArrow,
      handleShiftSelection,
      handleCopy,
      handlePaste,
      plainTextMenuItem,
      blockLink,
      unsupported,
      audio,
      file,
      oneDrive,
      oneDriveBusiness,
      googleDrive,
      codeBlock,
      latex,
      handleReturn(onReturn),
      list(onReturn),
    ];

    return composePlugins(
      [
        ...defaultPlugins,
        ...(plugins || []),
        ...(!readOnly
          ? [dragHandle, blockMenu, rootElement, handleDrop, voidEditing]
          : []),
      ],

      editor,
      {
        config,
        // We intentionally don't pass entities here because
        // it causes unnecessary paint cycles. See https://github.com/AulaEducation/aula/pull/11616#issuecomment-820436101
        //
        // Update: Added back to allow onReturn handler to have access to editor state and entities.
        // The drawback here is that any update on entities will re-compose plugins, and the reward
        // is that onReturn callback can be memoized without depending on editorState changes.
        // See: https://github.com/AulaEducation/aula/pull/12509#discussion_r640370976
        entities,
        createEntity,
        updateEntity,
        deleteEntity,
        draggingElement,
        setDraggingElement,
        setRecordingPath,
        recordingPath,
        setTemplatesPath,
        templatesPath,
        templates,
        contentType,
      },
    );
  }, [
    onReturn,
    plugins,
    readOnly,
    editor,
    config,
    entities,
    createEntity,
    updateEntity,
    deleteEntity,
    draggingElement,
    setDraggingElement,
    setRecordingPath,
    setTemplatesPath,
    templates,
    contentType,
  ]);

  const [shouldDisplayInlineLinkMenu, setShouldDisplayInlineLinkMenu] =
    useState(false);

  // we only want to toggle the inline link menu when selection has changed,
  // otherwise it will be impossible to close the menu without losing the original
  // selection
  useEffect(() => {
    setShouldDisplayInlineLinkMenu(
      isInlineLink(getCurrentFragment(editor)[0]) && !hasSelection(editor),
    );
  }, [editor, editor.selection]);

  const renderControls = () => {
    if (!readOnly && actionButton) {
      const isDisabled = actionButton.disabled;
      if (isDisabled && actionButton.hideWhenBlurred) return null;
      return (
        <ControlsContainer>
          {actionButton && actionButton.label && actionButton.onClick && (
            <EditorActionButton {...actionButton} disabled={isDisabled} />
          )}
        </ControlsContainer>
      );
    }
    return null;
  };

  const isEditorEmpty =
    editor.children.length === 1 &&
    Editor.isEmpty(editor, editor.children[0] as Element);

  /**
   * This effect is responsible for setting the cursor position to either
   *  start or end of the initial content, based cursorPosition prop.
   *
   *  Update: Please see the discussion on how this part effects performance:
   *  https://github.com/AulaEducation/aula/pull/13086#discussion_r661591337
   */
  useEffect(() => {
    if (readOnly) {
      return;
    }
    const path =
      cursorPosition === 'start'
        ? Editor.start(editor, [0, 0])
        : Editor.end(editor, []);
    Transforms.select(editor, path);
  }, [cursorPosition, editor, readOnly]);

  const handleScrollIntoView = useCallback((_, range) => {
    const leafElement = range?.startContainer?.parentElement;

    if (!leafElement) return;

    leafElement.scrollIntoView({
      block: 'nearest',
    });
  }, []);

  /*
   * When the user blurs the editor to focus on something else in the web page,
   * we need to clear the editor selection because there are helper components
   * whose rendering logic depend on it (e.g. the hovering toolbar).
   *
   * However, if the user moves to a different tab in the browser or a different
   * app in the OS, we don't want to clear the selection, so that the user can
   * get back to the web page and keep writing in the editor, right where they
   * left their cursor.
   */
  const { run, cancel } = useEffectIfWindowIsFocused({
    effectIfFocused: () => {
      Transforms.deselect(editor);
    },
    runIf: !readOnly,
  });

  return (
    <EditorWrapper
      isReadOnly={!!readOnly}
      onFocusCapture={cancel}
      onBlurCapture={run}
    >
      {
        // We need to unmount hovering toolbar
        // whenever possible for better performance, especially
        // in cases where multiple instances of editor coexist.
        !readOnly && (
          <>
            {shouldDisplayInlineLinkMenu && (
              <InlineLink
                onClose={() => setShouldDisplayInlineLinkMenu(false)}
              />
            )}
            {!isEditorEmpty && <HoveringToolbar />}
            {recordingPath != null && <AudioModal context={context} />}
            {templatesPath != null && TemplatesDrawer && (
              <TemplatesDrawer context={context} templates={templates} />
            )}
            <DropTarget
              onDrop={(event) => {
                handleBlockMove(editor, event, [0]);
                insertFilesFromEvent(editor, event, context, startLocation);
              }}
            >
              <Spacer />
            </DropTarget>
          </>
        )
      }
      <Editable
        scrollSelectionIntoView={handleScrollIntoView}
        renderPlaceholder={({ attributes, children }) => (
          <span
            {...attributes}
            style={{
              ...attributes.style,
              display: 'inline',
            }}
          >
            {children}
          </span>
        )}
        autoFocus={autoFocus}
        spellCheck
        readOnly={readOnly}
        placeholder={readOnly ? undefined : placeholder}
        {...editorProps}
      />
      {renderControls()}
    </EditorWrapper>
  );
};

/**
 * For better performance, the passed `onChange` and `onReturn` should be memoized with a `useCallback` hook
 */
export const AulaEditorWithoutTemplates = React.forwardRef(
  (
    {
      editorState: editorStateOuter,
      onChange,
      onReturn,
      plugins,
      config = {
        filesUrl: '',
        accessToken: '',
        institution: '',
        apiUrl: '',
        spaceId: '',
        userId: '',
        assignmentId: '',
        conversationId: '',
        postId: '',
        materialId: '',
        ltiProviders: [],
        itemType: '',
        correlationId: '',
      },
      placeholder,
      editor = createEditor(),
      readOnly = false,
      actionButton,
      cursorPosition,
      autoFocus = true,
      templates = defaultTemplates,
      contentType = '',
      templatesDrawer = undefined,
    }: AulaEditorProps,
    ref,
  ) => {
    const [hasMounted, setHasMounted] = useState<boolean>(false);
    const editorState = useMemo<EditorState>(() => {
      if (editorStateOuter == null) {
        throw new Error('editorState must not be null or undefined');
      }

      if (!isAulaEditorContent(editorStateOuter)) {
        const convertedEditorState = convertLegacyContent(editorStateOuter);

        // notify parent after converting legacy content, otherwise it will still
        // have the old editor format until the user types something.
        onChange?.(convertedEditorState);
        return convertedEditorState;
      }

      if (!hasMounted) {
        // sometimes mounting is done with empty state and
        // actual content is added to state asynchronously
        // the following check makes sure that convertUnsupported
        // is called after actual content is added
        const isEditorEmpty =
          editorStateOuter.value.length === 1 &&
          Editor.isEmpty(editor, editorStateOuter.value[0] as Element);
        if (isEditorEmpty) {
          return editorStateOuter;
        }
        setHasMounted(true);
        return convertUnsupported(editorStateOuter);
      }

      return editorStateOuter;
    }, [editorStateOuter, onChange]);

    const { value, context } = editorState;

    const editorRef = useRef<AulaEditorInstance>(
      withHistory(withReact(editor || createEditor())),
    );

    const onChangeContextState = (nextContext: ContextState) => {
      onChange?.({
        value,
        context: nextContext,
      });
    };

    const contextRef = useRef({
      onChange: onChangeContextState,
      context,
    });

    contextRef.current = {
      onChange: onChangeContextState,
      context,
    };

    const onChangeValue = useCallback(
      (nextValue) => {
        onChange?.({
          context,
          value: nextValue,
        });
      },
      [onChange, context],
    );

    const handlePlaceholder = () => {
      // Empty lists should not show placeholder
      const type = (value?.[0] as any)?.type;
      if ((type && type === orderedListType) || type === unorderedListType) {
        return ' ';
      }
      return placeholder;
    };

    React.useImperativeHandle(ref, () => ({
      focus: () => ReactEditor.focus(editorRef.current),
    }));

    /**
     * In Slate 0.67.0 the Slate component became uncontrolled, meaning that
     * the value prop is now only used as an initial value, and any changes to the value prop
     * are not picked up, so you cannot update Slate state this way
     * (see https://github.com/ianstormtaylor/slate/issues/4612 for discussions on this issue).
     *
     * As a workaround, if there is a mismatch between current editor children and the value prop,
     * we are explicitly setting the editor children to the new value and forcing Slate to re-render.
     *
     * One example where this is necessary is the TemplatesDrawer, where we switch the editor contents based
     * on the currently selected template option.
     */
    const forceUpdate = useForceUpdate();
    useEffect(() => {
      /* We only want to force update for controlled components.
      If the editor is readOnly, we can assume it is bound to some state above. */
      if (readOnly && editorRef.current.children !== value) {
        editorRef.current.children = value;
        forceUpdate();
      }
    }, [readOnly, value, forceUpdate]);

    return (
      <Slate editor={editorRef.current} value={value} onChange={onChangeValue}>
        <AulaEditorContextProvider
          contextRef={contextRef}
          config={config}
          templates={templates}
          contentType={contentType}
        >
          <AulaEditable
            editor={editorRef.current}
            plugins={plugins}
            readOnly={readOnly}
            placeholder={handlePlaceholder()}
            onReturn={onReturn}
            actionButton={actionButton}
            cursorPosition={cursorPosition}
            autoFocus={autoFocus}
            templatesDrawer={templatesDrawer}
          />
        </AulaEditorContextProvider>
      </Slate>
    );
  },
);

AulaEditorWithoutTemplates.displayName = 'AulaEditorWithoutTemplates';
